import React, { useState } from 'react'
import { Icon } from '@chakra-ui/icons'
import { AiFillPushpin, AiOutlinePushpin } from 'react-icons/ai'

import ConfirmModal, { ModalType } from '~components/shared/confirm-modal'
import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'
import { usePinnedPages } from './pinned-pages-provider'

interface Props {
    to: string
    label: string
    sectionLabel: string
}

/**
 * Pin/unpin toggle for a sidebar page. Pinning adds the page to the Pinned
 * submenu at the top (it stays in its home section too), so both directions
 * ask for confirmation first.
 */
const PinPageButton: React.FC<Props> = ({ to, label, sectionLabel }) => {
    const { isPinned, pinPage, unpinPage } = usePinnedPages()
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)

    const pinned = isPinned(to)
    const helpText = getHelpText(pinned, sectionLabel)

    return (
        <>
            <IconButtonWithTooltip
                label={pinned ? `Unpin ${label}` : `Pin ${label}`}
                variant="ghost"
                size="xs"
                rounded="full"
                color={pinned ? 'brand.500' : 'gray.500'}
                _hover={{ color: 'white', bg: 'gray.700' }}
                icon={
                    <Icon
                        as={pinned ? AiFillPushpin : AiOutlinePushpin}
                        fontSize="14px"
                    />
                }
                onClick={e => {
                    // Don't follow the row's link when (un)pinning.
                    e.preventDefault()
                    e.stopPropagation()
                    setIsConfirmOpen(true)
                }}
            />
            <ConfirmModal
                type={ModalType.Confirm}
                actionWord={pinned ? 'unpin' : 'pin'}
                itemType={label}
                headerOverride={
                    pinned
                        ? `Are you sure you want to unpin ${label}?`
                        : `Are you sure you want to pin ${label} to Pinned?`
                }
                helpTextOverride={helpText}
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={() => {
                    if (pinned) unpinPage(to)
                    else pinPage(to)
                    setIsConfirmOpen(false)
                }}
            />
        </>
    )
}

export default PinPageButton

/**
 * Confirmation copy for (un)pinning. Unpinning names the home section where
 * the page stays reachable.
 */
function getHelpText(pinned: boolean, sectionLabel: string): string {
    if (pinned) {
        return `It will be removed from the Pinned section. You can still find it in the ${sectionLabel} section.`
    }
    return 'It will be added to the Pinned section at the top of the sidebar for quick access.'
}
